import { type Answer, postAnswer, useSalesContext } from "@expert/sales-tools";
import { Flex, Grid, Modal, Text } from "@mantine/core";
import { type JSX, useEffect, useState } from "react";
import { useTimer } from "react-timer-hook";

import { useAnalytics } from "@/analytics";
import { RadioCard } from "@/common-ui";
import { useLogger } from "@/sdk";

import type { Choice, Question } from "../types";

import { CompletedQuiz } from "./CompletedQuiz";
import { FeedbackSection } from "./FeedbackSection";
import { CorrectAnswer, DollarSign, WrongAnswer } from "./icons";
import classes from "./MicroLearning.module.css";
import { ProgressBar } from "./ProgressBar";

interface MicroLearningModalProps {
    isOpen: boolean;
    close: () => void;
    invalidateQuizQuestion: () => Promise<void>;
    setCorrectAnswers: (count: number) => void;
    question: Question;
    topic: string;
    totalNumberOfQuestions: number;
    currentQuestionNumber: number;
}

export function MicroLearningModal({
    isOpen,
    close,
    invalidateQuizQuestion,
    setCorrectAnswers,
    question,
    topic,
    totalNumberOfQuestions,
    currentQuestionNumber,
}: MicroLearningModalProps): JSX.Element {
    const logger = useLogger({ module: "MicroLearningModal", supportTeam: "sales" });
    const { apiClients } = useSalesContext();
    const { choices, question: questionText, questionId } = question;

    const { dispatcher } = useAnalytics();

    const [selectedChoice, setSelectedChoice] = useState<Choice | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [completed, setCompleted] = useState(false);

    useEffect(() => {
        return () => {
            if (selectedChoice != null) {
                void invalidateQuizQuestion();
            }
        };
    }, [selectedChoice, invalidateQuizQuestion]);

    const handleClose = () => {
        close();
        void dispatcher.dispatchBusinessEvent("Click_CloseMicroLearningModal", {
            questionId,
            question: questionText,
            answer: selectedChoice?.choice,
            wasAnsweredCorrectly: selectedChoice?.isCorrectAnswer ?? "Ignored the question",
        });
    };

    const { start: startFeedbackTimer } = useTimer({
        expiryTimestamp: new Date(Date.now() + 4000),
        autoStart: false,
        onExpire: () => setShowFeedback(true),
    });

    const onChoiceSelected = (choice: Choice) => {
        if (selectedChoice != null) {
            return;
        }

        const answer: Answer = {
            questionId,
            isCorrectAnswer: choice?.isCorrectAnswer ?? false,
            topic,
        };
        void postAnswer(apiClients.gamification, logger, answer);

        void dispatcher.dispatchBusinessEvent("Click_Choice", {
            questionId,
            question: questionText,
            answer: choice.choice,
            wasAnsweredCorrectly: choice.isCorrectAnswer,
        });

        if (choice.isCorrectAnswer) {
            startFeedbackTimer();
            setCorrectAnswers(currentQuestionNumber + 1);
        }

        setSelectedChoice(choice);

        if (totalNumberOfQuestions - 1 === currentQuestionNumber && choice.isCorrectAnswer) {
            setCompleted(true);
        }
    };

    const getClass = ({ choice, isCorrectAnswer }: Choice) => {
        if (selectedChoice?.choice !== choice) {
            return "";
        } else if (isCorrectAnswer) {
            return classes.correct;
        }
        return classes.wrong;
    };

    const getColor = ({ choice, isCorrectAnswer }: Choice) => {
        if (selectedChoice?.choice !== choice) {
            return "dark.5";
        } else if (isCorrectAnswer) {
            return "success.5";
        } else {
            return "error.5";
        }
    };

    return (
        <Modal
            maw="500px"
            zIndex="var(--mantine-priority-highest)"
            centered
            data-testid="micro-learning-modal"
            opened={isOpen}
            onClose={handleClose}
            withCloseButton={false}
            padding={0}
            transitionProps={{
                transition: "pop",
            }}
            overlayProps={{
                backgroundOpacity: 0.4,
            }}
            classNames={{ header: classes.header, content: classes.content }}
        >
            <Flex className={classes.top}>
                <Flex className={classes.header} h={47} gap="xs">
                    <Flex>
                        <DollarSign />
                        <Text c="primary.1" pl={6} fz="sm" className={classes.title}>
                            Knowledge check
                        </Text>
                    </Flex>
                    <Flex pr="md">
                        <ProgressBar
                            currentQuestionNumber={currentQuestionNumber}
                            totalNumberOfQuestions={totalNumberOfQuestions}
                            progressWidth={320}
                        />
                    </Flex>
                </Flex>
                <Flex className={classes.body} h="fit-content" wrap={"wrap"}>
                    {completed ? (
                        <CompletedQuiz onClickClose={handleClose} />
                    ) : (
                        <>
                            <Flex fz={18} className={classes.question} maw="500px">
                                {questionText}
                            </Flex>
                            <Grid maw="500px" pt="md" pb="md" justify="flex-start" align="flex-start">
                                {choices.map((choice) => {
                                    return (
                                        <Grid.Col span="content" maw="500px" key={choice.choice}>
                                            <RadioCard
                                                key={choice.choice}
                                                disabled={
                                                    selectedChoice != null && selectedChoice?.choice !== choice.choice
                                                }
                                                className={getClass(choice)}
                                                iconColor="dark.5"
                                                radioColor={getColor(choice)}
                                                label={choice.choice}
                                                value={choice.choice}
                                                color="transparent"
                                                p="0.5rem 0.75rem"
                                                selected={selectedChoice?.choice === choice.choice}
                                                onClick={() => onChoiceSelected(choice)}
                                                analytics={{
                                                    actionId: "",
                                                }}
                                            />
                                        </Grid.Col>
                                    );
                                })}
                            </Grid>
                            {selectedChoice && (
                                <Flex pb="md">
                                    <Flex>
                                        {selectedChoice.isCorrectAnswer ? <CorrectAnswer /> : <WrongAnswer />}
                                        <Text fw="700" pl="xxs" className={classes.answerText}>
                                            {selectedChoice.confirmationText
                                                ? selectedChoice.confirmationText
                                                : selectedChoice.isCorrectAnswer
                                                  ? "Correct!"
                                                  : "Incorrect!"}
                                        </Text>
                                    </Flex>
                                </Flex>
                            )}
                            <Flex
                                w="100%"
                                h={selectedChoice?.isCorrectAnswer && showFeedback ? 68 : 0}
                                className={
                                    selectedChoice?.isCorrectAnswer && showFeedback
                                        ? classes.feedbackDown
                                        : classes.feedbackUp
                                }
                            >
                                {selectedChoice?.isCorrectAnswer && showFeedback && (
                                    <FeedbackSection
                                        hideSection={() => {
                                            setShowFeedback(false);
                                        }}
                                        question={questionText}
                                    />
                                )}
                            </Flex>
                        </>
                    )}
                </Flex>
            </Flex>
        </Modal>
    );
}
